import { useCallback, useContext, useEffect, useMemo, useRef, useState, MouseEvent, ReactNode } from "react";

import { useMutation } from "@apollo/client";
import { Divider, Input, message } from "antd";
import { clsx } from "clsx";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { RVIcon, RVIconSizeOptions, DateFormat, humanizedDuration, RVButton } from "@regal-voice/ui";
import { UserContext } from "App/contexts";
import { getJourneyNodeIcon } from "Components/elements/RVFlow/Shared/JourneyNodePresentation";
import { Column, Row } from "Components/shared/Flexbox";
import { useInputHotkey } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/hooks/useMessageInputHotkey";
import { isNewJourney } from "Pages/orchestration/Pages/journeys/JourneyEditPage/helpers";
import { renderErrorMessage } from "Services/LoggingService";
import { upsertJourneysStickyNote, deleteJourneysStickyNote } from "Services/marketing-api/journeys/mutations";
import { selectJourneyNote, updateNote } from "Services/state/journeys/JourneysNotesStateSlice";
import {
    removeElementById,
    editStickyNote,
    updateStickyNote,
    selectExpandedStickyNoteNodes,
    collapseStickyNote,
    selectJourneyId,
} from "Services/state/journeys/JourneyStateSlice";
import { ReactFlowNodeData, XYPosition, NodeType } from "Types/Journey";

import styles from "./StickyNoteBlock.module.scss";

export type Note = {
    id: string;
    text: string;
    author: string;
    date: string;
};

export type StickyNoteBlockProps = {
    id: string;
    data: any;
    xPos: number;
    yPos: number;
};

export type NoteListProps = {
    id: string;
    notes: { id: string; text: string; author: string; date: any }[];
    editing: boolean;
    position: XYPosition;
    setEditing: (editingMode: boolean) => void;
    closeNote: () => void;
};

// TODO: Why doesnt this use the FlowNodeBlocks like the other blocks?
const BLOCK_ICON = getJourneyNodeIcon(NodeType.StickyNote);

export function StickyNoteBlock({ id, data, xPos, yPos }: StickyNoteBlockProps): JSX.Element {
    const dispatch = useDispatch();

    // expanded state from "expand all" button
    // or note node click
    const expandedNotes = useSelector(selectExpandedStickyNoteNodes);
    // edit mode triggered by edit button or click on note in view more
    // also is initially true if there are no notes
    const [editMode, setEditMode] = useState(data?.notes == null || data?.notes?.length == 0);
    // popover visibility triggered by mouse hover
    const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
    const expanded = expandedNotes[id];

    const journeyId = useSelector(selectJourneyId);
    const journeyNotes = useSelector(selectJourneyNote)(journeyId);
    const note = journeyNotes[id];
    const editing = editMode || note != null;
    const showNote = popoverVisible || expanded;

    const setEditing = useCallback((mode: boolean) => setEditMode(mode), []);
    const closeNote = useCallback(() => {
        setEditMode(false);
        setPopoverVisible(false);
        dispatch(collapseStickyNote(id));
    }, [id, dispatch, setEditMode, setPopoverVisible]);
    const showPopover = useCallback(() => setPopoverVisible(true), []);
    const hidePopover = useCallback(() => setPopoverVisible(false), []);
    const editNote = useCallback(() => {
        dispatch(editStickyNote(id));
        showPopover();
    }, [showPopover, dispatch, id]);
    const position: XYPosition = { x: xPos, y: yPos };

    return (
        <div className={styles.contentBox}>
            <Row
                className={styles.noteIcon}
                onMouseLeave={hidePopover}
                onMouseEnter={showPopover}
                onFocus={showPopover}
                onClick={editNote}
            >
                {BLOCK_ICON}
            </Row>
            <FlowPopover visible={showNote}>
                <NotesList
                    id={id}
                    notes={data.notes}
                    editing={editing}
                    setEditing={setEditing}
                    closeNote={closeNote}
                    position={position}
                />
            </FlowPopover>
        </div>
    );
}

/* Popover that doesn't overflow the react-flow panel and moves with nodes when pane is moved/zoomed */
function FlowPopover({ visible, children }: { visible: boolean; children: ReactNode }) {
    return visible ? (
        <div className={styles.flowPanel}>
            <div className={clsx(styles.noteWrapper, { hidden: !visible })}>{children}</div>
        </div>
    ) : (
        <></>
    );
}

function NotesList({ id, notes, editing, position, setEditing, closeNote }: NoteListProps) {
    const { user } = useContext(UserContext);
    const dispatch = useDispatch();
    const inputRef = useRef<any>(null);
    const journeyId = useSelector(selectJourneyId);
    const journeyNotes = useSelector(selectJourneyNote)(journeyId);
    const note = journeyNotes[id] || notes?.[0]?.text || "";
    const [upsertNote] = useMutation(upsertJourneysStickyNote);

    const setNoteCb = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            dispatch(updateNote({ journeyId, id, note: e.target.value }));
        },
        [dispatch, journeyId, id]
    );

    const addNote = useCallback(
        (stickyNote: Note[]) => {
            dispatch(updateStickyNote(stickyNote));
            dispatch(updateNote({ journeyId, id, note: stickyNote[0].text }));
        },
        [dispatch, user, journeyId, id, note]
    );

    const saveChange = useCallback(() => {
        if (isNewJourney(journeyId)) {
            message.warning("Save new Journey first to submit notes");
        } else {
            const notes: Note[] = [
                { text: note, author: user?.name || "", id: uuidv4(), date: dayjs().utc().format(DateFormat.ISO_8601) },
            ];
            const stickyNoteNodeData: ReactFlowNodeData = {
                id,
                data: { notes },
                position,
            };

            // Save note to RDS table
            upsertNote({
                variables: {
                    journeyId,
                    stickyNote: stickyNoteNodeData,
                },
                onCompleted: () => {
                    message.success("Successfully saved note.");
                    // Once successfully saved, update state and close note
                    addNote(notes);
                    dispatch(updateNote({ journeyId, id, note: undefined }));
                    closeNote();
                },
                onError: (error) => renderErrorMessage({ content: `Error saving note: ${error.message}.`, error }),
            });
        }
    }, [addNote, closeNote, dispatch, upsertNote, journeyId, id, note]);

    const handleSaveChange = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            saveChange();
        },
        [saveChange]
    );

    const addNewLine = useCallback(() => {
        const value = note + "\n";
        dispatch(updateNote({ journeyId, id, note: value }));
    }, [dispatch, journeyId, id, note]);

    const editNote = useCallback(() => {
        setEditing(true);
    }, [setEditing]);

    useEffect(() => {
        if (editing && inputRef.current) {
            // set focus to input when editing to allow user to type immediately without clicking
            inputRef.current.focus();
        }
    }, [editing]);

    useInputHotkey(inputRef, "shift+enter", saveChange);
    useInputHotkey(inputRef, "ctrl+enter", saveChange);
    useInputHotkey(inputRef, "command+enter", saveChange);
    useInputHotkey(inputRef, "enter", addNewLine);

    return (
        <Column gap={2} className={styles.notesContainer}>
            <NoteHeader id={id} note={notes?.[0]} editing={editing} setEditing={setEditing} />
            {!editing ? (
                <Column onClick={editNote}>
                    {(notes || []).map((it) => (
                        <Note key={it.id} note={it} />
                    ))}
                </Column>
            ) : (
                <Column gap={2}>
                    <div className={styles.noteEditor}>
                        <div className={clsx(styles.inputWrapper)}>
                            <Input.TextArea
                                value={note}
                                ref={inputRef}
                                rows={2}
                                autoSize={{ minRows: 2 }}
                                onChange={setNoteCb}
                                placeholder="Write Note"
                                className="nodrag"
                            />
                        </div>
                        <RVButton type="primary" onClick={handleSaveChange} className={styles.saveButton}>
                            Submit
                        </RVButton>
                    </div>
                    <span className={clsx("subcopy", styles.inputHint)}>
                        <b>Return</b> to add a new line <b className={styles.rightHint}>CTRL + Return</b> to submit
                    </span>
                </Column>
            )}
        </Column>
    );
}

const MAX_TEXT_LENGTH = 283;
const MAX_ROWS = 5;

function initShowMore(text: string): boolean {
    const tooManyChars = text?.length > MAX_TEXT_LENGTH;
    const tooManyRows = text?.split("\n")?.length > MAX_ROWS;
    return tooManyChars || tooManyRows;
}

function Note({ note }: { note: Note }) {
    const { text } = note;
    const [showMore, setShowMore] = useState(initShowMore(text));
    const hasShowMore = useMemo(() => initShowMore(text), [text]);
    const toggleShowMore = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        setShowMore((show) => !show);
    }, []);

    const noteText = showMore ? text?.slice(0, MAX_TEXT_LENGTH) + "..." : text;
    const lines = noteText?.split("\n").slice(0, showMore ? MAX_ROWS : undefined) || [];
    return (
        <Column gap={1} className={styles.note}>
            <div className={styles.noteText}>
                {lines.map((line, idx) => (
                    <p key={idx} className={styles.noteLine}>
                        {line}
                        {idx === lines.length - 1 && hasShowMore && (
                            <a className={styles.showMore} onClick={toggleShowMore}>
                                See {showMore ? "More" : "Less"}
                            </a>
                        )}
                    </p>
                ))}
            </div>
        </Column>
    );
}

function NoteHeader({
    id,
    note,
    editing,
    setEditing,
}: {
    id: string;
    note?: Note;
    editing: boolean;
    setEditing: (editingMode: boolean) => void;
}) {
    const dispatch = useDispatch();
    const journeyId = useSelector(selectJourneyId);

    const [deleteNote] = useMutation(deleteJourneysStickyNote);

    const closeNote = useCallback(() => {
        dispatch(collapseStickyNote(id));
    }, [dispatch, id]);

    const removeNote = useCallback(() => {
        deleteNote({
            variables: {
                journeyId,
                nodeId: id,
            },
            onCompleted: () => {
                message.success("Successfully deleted note.");
                dispatch(removeElementById(id));
            },
            onError: (error) => renderErrorMessage({ content: `Error deleting note: ${error.message}.`, error }),
        });
    }, [id, dispatch]);

    const toggleEditing = useCallback(() => {
        setEditing(!editing);
    }, [setEditing, editing]);

    return (
        <Column>
            <Row justifyContent="flex-end" gap={3}>
                <NoteActionIcon icon="trash" color="red-4" onClick={removeNote} />
                {note && <NoteActionIcon icon="edit" onClick={toggleEditing} />}
                <NoteActionIcon icon="close" onClick={closeNote} />
            </Row>
            <Divider className={styles.divider} />
            {note && (
                <Row className={styles.noteHeader}>
                    <span className={styles.noteAuthor}>{note.author}</span>
                    <span className={styles.noteDate}>
                        {humanizedDuration(dayjs().unix() - dayjs(note.date).unix(), "seconds")} ago
                    </span>
                </Row>
            )}
        </Column>
    );
}

function NoteActionIcon({ icon, color, onClick }: { icon: string; color?: string; onClick: () => void }) {
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            onClick();
        },
        [onClick]
    );

    return (
        <RVButton
            type="text"
            className={styles.actionButton}
            icon={<RVIcon icon={icon} color={color} size={RVIconSizeOptions.SMALL} />}
            onClick={handleClick}
        />
    );
}
